import {
  currentLocale,
  currentTimeZone,
  formatToYMD,
  getDateTime,
} from 'infras/datetime';
import DashboardCalendarPage from 'presentations/views/components/pages/DashboardCalendarPage';
import { CalendarProvider } from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';
import {
  CalendarMode,
  ShowRange,
  SlotViewType,
  CalendarWeekDay,
} from 'presentations/views/components/atoms/Calendar/reducer';
import CalendarWeekDayFeature from 'domains/models/subscription/features/start_weekday';
import { useContext, useEffect, useMemo, useState } from 'react';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import { useRequest } from 'presentations/views/hooks/request';
import { apiPath } from 'presentations/routes/path';
import { getPersistentItemWithViewer } from 'presentations/views/helpers/storage';
import { isMediaMax } from 'presentations/views/styles';
import { useRouter } from 'next/router';
import {
  durationForCalendarKey,
  rulerStepForCalendarKey,
  showRangeForCalendarKey,
  // viewTypeForCalendarKey,
} from 'presentations/views/helpers/storage/config';
import { UserProfileItem } from 'presentations/api/schema/user';

export default function Page() {
  const { state } = useContext(DashboardPortalContext);

  const userProfileRequest = useRequest(apiPath(`/v1/user/profile`));
  const userProfile: UserProfileItem | null = userProfileRequest.data || null;

  const isMobile = isMediaMax('tablet');
  const router = useRouter();

  const currentDate = useMemo(() => {
    let c = undefined;
    if (router.query.day) {
      c = formatToYMD(getDateTime(String(router.query.day)).startOf('day'));
    }
    // Day → Monthの時
    else if (router.query.month) {
      c = formatToYMD(getDateTime(String(router.query.month)).startOf('month'));
    }
    return c;
  }, [router.isReady, router.query.day, router.query.month]);

  const duration = useMemo(() => {
    const duration = getPersistentItemWithViewer(
      durationForCalendarKey,
      state.viewer,
    );
    return duration || 30;
  }, []);

  const rulerStep = useMemo(() => {
    const intervalMinutes = getPersistentItemWithViewer(
      rulerStepForCalendarKey,
      state.viewer,
    );
    return intervalMinutes || 15;
  }, []);

  const {
    startWeekday,
    isShowSameEventMerge,
    isShowEventOverlap,
    isShowDeclinedEvent,
    isShowWeekends,
    calendarViewDays,
    timeFormat,
    timeZone,
    extraTimeZones,
  } = useMemo(() => {
    let startWeekday: CalendarWeekDay = 0;
    if (!userProfile || !state.viewer) {
      return {
        startWeekday,
      };
    }

    const startWeekDayFeature = new CalendarWeekDayFeature({
      planVersion: state.viewer.planVersion,
    });
    const isEnableStartWeekday = startWeekDayFeature.isValid(
      state.viewer.planId,
    );
    if (isEnableStartWeekday) {
      if (userProfile?.startWeekDay === 99) {
        startWeekday = getDateTime().weekday as CalendarWeekDay;
      } else {
        startWeekday = userProfile.startWeekDay as CalendarWeekDay;
      }
    }
    return {
      startWeekday,
      isShowSameEventMerge: userProfile.isShowSameEventMerge,
      isShowEventOverlap: userProfile.isShowEventOverlap,
      isShowDeclinedEvent: userProfile.isShowDeclinedEvent,
      isShowWeekends: userProfile.isShowWeekends,
      calendarViewDays: userProfile.calendarViewDays,
      timeFormat: userProfile.timeFormat,
      timeZone: userProfile.timeZone || currentTimeZone(),
      locale: userProfile.locale || currentLocale(),
      extraTimeZones: userProfile.extraTimeZones,
    };
  }, [userProfile, state.viewer]);

  const showRange = useMemo(() => {
    let view = calendarViewDays;
    if (isMobile) {
      view =
        getPersistentItemWithViewer(showRangeForCalendarKey, state.viewer) || 2;
    }
    if (router.query.view) {
      view = Number(router.query.view);
    }

    let range: ShowRange = { day: 7 };
    switch (view) {
      case 1:
        range = { day: 1 };
        break;
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
        range = { day: view };
        break;
      case 28:
        range = { month: 1 };
        break;
      default:
        break;
    }
    // Day → Monthの時
    if (router.query.month) {
      range = { month: 1 };
    }
    // Month → Dayの時
    if (router.query.day) {
      range = { day: 1 };
    }
    return range;
  }, [
    router.isReady,
    router.query.view,
    router.query.day,
    router.query.month,
    calendarViewDays,
    isMobile,
  ]);

  // const viewType = useMemo(() => {
  //   const vt = getPersistentItemWithViewer(
  //     viewTypeForCalendarKey,
  //     state.viewer,
  //   );
  //   return router.query.viewType || vt || EventCreateViewType.Calendar;
  // }, [router.query.viewType]);

  if (!userProfile) return null;

  return (
    <CalendarProvider
      cachePrefix={`${state.viewer!.team!.uid}-${state.viewer!.uid}`}
      duration={duration}
      rulerStep={rulerStep}
      rulerBorderStep={30}
      calendarMode={CalendarMode.Event}
      showRange={showRange}
      slotViewType={SlotViewType.Group}
      startWeekday={startWeekday}
      currentDate={currentDate}
      isShowSameEventMerge={isShowSameEventMerge}
      isShowEventOverlap={isShowEventOverlap}
      isShowDeclinedEvent={isShowDeclinedEvent}
      hiddenWeekdays={isShowWeekends ? [] : [0, 6]}
      timeFormat={timeFormat}
      rulerTimeZones={extraTimeZones}
      isDisableMultiTimeZones={isMobile}
      timeZone={timeZone}
      // onError={actions.onCalendarError}
    >
      <DashboardCalendarPage
        startWeekday={userProfile.startWeekDay as CalendarWeekDay}
      />
    </CalendarProvider>
  );
}
